/**
 * Minimal Supabase transport: PostgREST, RPC, Edge Functions and Storage over plain fetch. No supabase-js, so it
 * works in the sandbox, in the browser, in Deno Edge Functions and in node tests. `Rest` is the seam the adapters depend
 * on; tests pass an in-memory fake (tests/unit/helpers/memory-rest.ts), production passes createFetchRest().
 */
export type Row = Record<string, unknown>;
export type Scalar = string | number | boolean;

export interface RestQuery {
  /** PostgREST select list, e.g. "id,name,slug_aliases(slug,until)". Omit for all columns (not allowed on token tables for owners). */
  select?: string;
  eq?: Record<string, Scalar>;
  gte?: Record<string, Scalar>;
  lt?: Record<string, Scalar>;
  order?: string;
  limit?: number;
}

export class RestError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string, readonly details?: unknown) {
    super(message);
    this.name = 'RestError';
  }
  /** Postgres exclusion violation: the slot was taken by someone else a moment ago. */
  get isSlotTaken(): boolean { return this.code === '23P01'; }
  /** apply_batch precondition failed (a row we read changed): re-run the callback. */
  get isConflict(): boolean { return this.code === '40001' || this.message.includes('precondition_failed'); }
  get isUnauthorized(): boolean { return this.status === 401 || this.code === 'PGRST301' || this.code === 'PGRST303'; }
}

export interface Rest {
  select(table: string, q?: RestQuery): Promise<Row[]>;
  /** Insert or update by `onConflict` column (default id). Missing columns are left untouched on update. */
  upsert(table: string, rows: Row[], opts?: { onConflict?: string }): Promise<void>;
  remove(table: string, eq: Record<string, Scalar>): Promise<void>;
  rpc<T = unknown>(fn: string, args?: Record<string, unknown>): Promise<T>;
  /** POST /functions/v1/<name>. `auth: 'anon'` sends only the anon key (public booking), 'user' sends the session JWT when present. */
  invoke<T = unknown>(fn: string, body: unknown, opts?: { auth?: 'anon' | 'user' }): Promise<T>;
  storage: {
    upload(bucket: string, path: string, bytes: Uint8Array, contentType: string): Promise<void>;
    remove(bucket: string, paths: string[]): Promise<void>;
    publicUrl(bucket: string, path: string): string;
  };
}

export interface FetchRestOptions {
  url: string;
  anonKey: string;
  /** Current user JWT (access token) or undefined for anonymous. */
  getToken: () => string | undefined;
  fetch?: typeof fetch;
  /** PostgREST caps responses (Supabase default max_rows 1000): list() pages through with offset. */
  pageSize?: number;
}

const PAGE = 1000;

export function createFetchRest(opts: FetchRestOptions): Rest {
  const base = opts.url.replace(/\/+$/, '');
  const f = opts.fetch ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a));
  const page = opts.pageSize ?? PAGE;
  const headers = (extra: Record<string, string> = {}, anonymous = false): Record<string, string> => {
    const jwt = anonymous ? undefined : opts.getToken();
    return { apikey: opts.anonKey, Authorization: `Bearer ${jwt ?? opts.anonKey}`, ...extra };
  };
  async function parse(res: Response): Promise<unknown> {
    const text = await res.text();
    let body: unknown = undefined;
    if (text) { try { body = JSON.parse(text); } catch { body = text; } }
    if (!res.ok) {
      const b = (body ?? {}) as { code?: string; message?: string; error?: string; msg?: string; details?: unknown; error_description?: string };
      throw new RestError(b.message ?? b.msg ?? b.error_description ?? b.error ?? `HTTP ${res.status}`, res.status, b.code ?? b.error, b.details);
    }
    return body;
  }
  function qs(q: RestQuery, extra: [string, string][] = []): string {
    const sp = new URLSearchParams();
    sp.set('select', q.select ?? '*');
    for (const [k, v] of Object.entries(q.eq ?? {})) sp.append(k, `eq.${String(v)}`);
    for (const [k, v] of Object.entries(q.gte ?? {})) sp.append(k, `gte.${String(v)}`);
    for (const [k, v] of Object.entries(q.lt ?? {})) sp.append(k, `lt.${String(v)}`);
    if (q.order) sp.set('order', q.order);
    for (const [k, v] of extra) sp.set(k, v);
    return sp.toString();
  }
  return {
    async select(table, q = {}) {
      if (q.limit !== undefined) return (await parse(await f(`${base}/rest/v1/${table}?${qs(q, [['limit', String(q.limit)]])}`, { headers: headers() }))) as Row[];
      const out: Row[] = [];
      for (let offset = 0; ; offset += page) {
        const rows = (await parse(await f(`${base}/rest/v1/${table}?${qs({ ...q, order: q.order ?? 'id' }, [['limit', String(page)], ['offset', String(offset)]])}`, { headers: headers() }))) as Row[];
        out.push(...rows);
        if (rows.length < page) return out;
      }
    },
    async upsert(table, rows, o = {}) {
      if (rows.length === 0) return;
      await parse(await f(`${base}/rest/v1/${table}?on_conflict=${encodeURIComponent(o.onConflict ?? 'id')}`, {
        method: 'POST', headers: headers({ 'Content-Type': 'application/json', Prefer: 'resolution=merge-duplicates,return=minimal' }), body: JSON.stringify(rows),
      }));
    },
    async remove(table, eq) {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(eq)) sp.append(k, `eq.${String(v)}`);
      await parse(await f(`${base}/rest/v1/${table}?${sp.toString()}`, { method: 'DELETE', headers: headers({ Prefer: 'return=minimal' }) }));
    },
    async rpc(fn, args = {}) {
      return (await parse(await f(`${base}/rest/v1/rpc/${fn}`, { method: 'POST', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify(args) }))) as never;
    },
    async invoke(fn, body, o = {}) {
      return (await parse(await f(`${base}/functions/v1/${fn}`, { method: 'POST', headers: headers({ 'Content-Type': 'application/json' }, o.auth === 'anon'), body: JSON.stringify(body) }))) as never;
    },
    storage: {
      async upload(bucket, path, bytes, contentType) {
        await parse(await f(`${base}/storage/v1/object/${bucket}/${path.split('/').map(encodeURIComponent).join('/')}`, {
          method: 'POST', headers: headers({ 'Content-Type': contentType, 'x-upsert': 'true' }), body: bytes as unknown as BodyInit,
        }));
      },
      async remove(bucket, paths) {
        if (paths.length === 0) return;
        await parse(await f(`${base}/storage/v1/object/${bucket}`, { method: 'DELETE', headers: headers({ 'Content-Type': 'application/json' }), body: JSON.stringify({ prefixes: paths }) }));
      },
      publicUrl: (bucket, path) => `${base}/storage/v1/object/public/${bucket}/${path.split('/').map(encodeURIComponent).join('/')}`,
    },
  };
}
